import { DEFAULT_INSTANCE_NAME } from '@kanto/shared';
import type { ReactElement } from 'react';

/**
 * The name this instance goes by.
 *
 * Read from the `application-name` meta tag rather than fetched, because that
 * tag is one of the three slots the server substitutes when it serves
 * `index.html`: the name is therefore correct before the first request, and
 * there is no moment where the screen says one thing and the tab another. Under
 * `pnpm dev`, where Vite serves the file untouched, the tag still holds the
 * built-in default and so does this.
 */
function instanceName(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="application-name"]');
  return meta?.content?.trim() || DEFAULT_INSTANCE_NAME;
}

/**
 * The mark, and the instance name beside it.
 *
 * One component for both places the application introduces itself — the sign-in
 * screen and the top bar — because they have to show the *same* thing. Two
 * copies would eventually disagree, and the one that disagreed would be the one
 * nobody looks at.
 *
 * The mark is inline rather than an `<img>`: it is four shapes, it has to stay
 * crisp from 28 px to 56 px, and inlining spares the sign-in screen a second
 * request before it can render. Its geometry is transcribed from
 * `assets/kanto-mark.svg`, which is the source the icons are generated from
 * (D260814g) — change one and change the other.
 */
export function Brand({
  size = 'sm',
  name = true,
  className = '',
}: {
  /** `sm` sits in the top bar; `lg` above the sign-in heading. */
  size?: 'sm' | 'lg';
  /**
   * Whether the name is written beside the mark. The rail is 68 px wide and has
   * no room for it, and dropping it there rather than transcribing the mark a
   * second time is what keeps the two from ever disagreeing.
   */
  name?: boolean;
  className?: string;
}): ReactElement {
  const large = size === 'lg';

  return (
    <span className={`flex items-center gap-2.5 ${className}`}>
      <svg
        viewBox="0 0 512 512"
        className={large ? 'size-14' : 'size-8'}
        // Decorative: the name is written beside it as text, and hearing it
        // twice helps nobody. Without the name, the `sr-only` span below is what
        // says it instead.
        aria-hidden="true"
        focusable="false"
      >
        <rect x="8" y="8" width="496" height="496" rx="112" className="fill-surface" />
        <rect
          x="8"
          y="8"
          width="496"
          height="496"
          rx="112"
          fill="none"
          strokeWidth="16"
          className="stroke-line-strong"
        />
        <g
          fill="none"
          strokeWidth="44"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="stroke-accent"
        >
          <path d="M156 140 V 356" />
          <path d="M156 282 L 236 362 L 370 172" />
        </g>
      </svg>
      <span
        className={
          name
            ? `font-semibold tracking-tight text-ink-900 ${large ? 'text-3xl' : 'text-lg'}`
            : 'sr-only'
        }
      >
        {instanceName()}
      </span>
    </span>
  );
}
